class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchRequestId = 0;
    this.searchDeferred = null;
    this.searchTimeout = null;
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('[data-monoree-search-groups]')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.cancelSearchRequest();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.cancelSearchRequest();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (!previousTerm || currentButtonText.split(previousTerm).length - 1 > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  cancelSearchRequest() {
    this.searchRequestId += 1;
    this.abortController.abort();
    clearTimeout(this.searchTimeout);
    this.searchTimeout = null;
    if (this.searchDeferred) {
      this.searchDeferred.promise?.catch(() => {});
      this.searchDeferred.reject(new DOMException('Search changed', 'AbortError'));
      this.searchDeferred = null;
    }
  }

  disconnectedCallback() {
    this.cancelSearchRequest();
  }

  getSearchResults(searchTerm) {
    this.cancelSearchRequest();
    const requestId = this.searchRequestId;
    const queryKey = searchTerm.trim().toLowerCase();
    const controller = new AbortController();
    this.abortController = controller;
    this.setLiveRegionLoadingState();

    const cachedMarkup = Object.prototype.hasOwnProperty.call(this.cachedResults, queryKey)
      ? this.cachedResults[queryKey]
      : null;
    const hasCachedResults = typeof cachedMarkup === 'string' &&
      new DOMParser().parseFromString(cachedMarkup, 'text/html')
        .querySelector('[data-predictive-search-live-region-count-value]');
    if (!hasCachedResults) {
      this.allPredictiveSearchInstances.forEach((instance) => delete instance.cachedResults[queryKey]);
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
    this.searchDeferred = searchDeferred;
    let timeout;
    const responsePromise = hasCachedResults ? Promise.resolve(cachedMarkup) : fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Search request failed (${response.status})`);
        return response.text();
      });
    const abortPromise = new Promise((resolve, reject) => {
      controller.signal.addEventListener('abort', () => reject(new DOMException('Search changed', 'AbortError')), { once: true });
    });
    const timeoutPromise = new Promise((resolve, reject) => {
      timeout = setTimeout(() => {
        reject(new Error('Search request timed out'));
        controller.abort();
      }, 15000);
      this.searchTimeout = timeout;
    });
    return Promise.race([responsePromise, abortPromise, timeoutPromise])
      .then((text) => {
        if (requestId !== this.searchRequestId || this.getQuery() !== searchTerm) return;
        let resultsMarkup = text;
        if (!hasCachedResults) {
          const resultsSection = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search');
          if (!resultsSection?.querySelector('[data-predictive-search-live-region-count-value]')) {
            throw new Error('Missing predictive search response');
          }
          resultsMarkup = resultsSection.innerHTML;
        }
        this.renderSearchResults(resultsMarkup);
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
        this.searchDeferred = null;
      })
      .catch((error) => {
        if (requestId !== this.searchRequestId) return;
        searchDeferred?.promise?.catch(() => {});
        searchDeferred?.reject(error);
        this.searchDeferred = null;
        this.allPredictiveSearchInstances.forEach((instance) => delete instance.cachedResults[queryKey]);
        this.removeAttribute('results');
        this.predictiveSearchResults.replaceChildren();
        this.close();
      })
      .finally(() => {
        clearTimeout(timeout);
        if (requestId === this.searchRequestId) this.searchTimeout = null;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    // Each search field owns its own IDs, including cached results shared between instances.
    const idMap = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach((element) => {
      const previousId = element.id;
      if (previousId === 'predictive-search-results-groups-wrapper') {
        element.setAttribute('data-monoree-search-groups', '');
      }
      element.id = `${previousId}-${this.input.id}`;
      idMap.set(previousId, element.id);
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls]').forEach((element) => {
      ['aria-labelledby', 'aria-describedby', 'aria-controls'].forEach((attribute) => {
        if (!element.hasAttribute(attribute)) return;
        const ids = element.getAttribute(attribute).split(/\s+/).map((id) => idMap.get(id) || id);
        element.setAttribute(attribute, ids.join(' '));
      });
    });
    const resultsId = idMap.get('predictive-search-results');
    if (resultsId) this.input.setAttribute('aria-controls', resultsId);
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
